import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Variant style mappings for icon buttons
 */
const VARIANT_STYLES = {
  ghost: "hover:bg-cede-bg-component",
  solid: "bg-cede-bg-component hover:bg-cede-bg-secondary",
  outline: "border border-cede-hr hover:border-cede-primary hover:bg-cede-bg-component",
  primary: "bg-cede-primary hover:bg-cede-primary/90 text-white",
} as const;

/**
 * Size mappings for icon buttons
 */
const SIZE_STYLES = {
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-12 w-12",
} as const;

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Icon element to display
   */
  icon: React.ReactNode;

  /**
   * Accessible label (required for screen readers)
   */
  label: string;

  /**
   * Visual variant
   * @default "ghost"
   */
  variant?: keyof typeof VARIANT_STYLES;

  /**
   * Button size
   * @default "md"
   */
  size?: keyof typeof SIZE_STYLES;

  /**
   * Show notification badge
   */
  badge?: number | boolean;

  /**
   * Badge color (Tailwind class)
   * @default "bg-cede-error"
   */
  badgeColor?: string;

  /**
   * Show tooltip on hover
   * @default false
   */
  showTooltip?: boolean;

  /**
   * Additional className
   */
  className?: string;
}

/**
 * Icon button component with proper button semantics and accessibility
 *
 * @example
 * ```tsx
 * // Basic icon button
 * <IconButton
 *   icon={<DiscordIcon />}
 *   label="Join Discord"
 *   onClick={() => window.open(DISCORD_URL)}
 * />
 *
 * // With variant and badge
 * <IconButton
 *   icon={<BellIcon />}
 *   label="Notifications"
 *   variant="solid"
 *   badge={5}
 *   onClick={openNotifications}
 * />
 *
 * // Primary style
 * <IconButton
 *   icon={<PlusIcon />}
 *   label="Add"
 *   variant="primary"
 *   onClick={handleAdd}
 * />
 * ```
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      label,
      variant = "ghost",
      size = "md",
      badge,
      badgeColor = "bg-cede-error",
      showTooltip = false,
      className,
      disabled,
      ...props
    },
    ref,
  ) => {
    return (
      <div className="relative inline-flex" title={showTooltip ? label : undefined}>
        <button
          ref={ref}
          type="button"
          className={cn(
            "inline-flex items-center justify-center rounded-full",
            "transition-colors duration-200",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cede-primary",
            SIZE_STYLES[size],
            VARIANT_STYLES[variant],
            disabled && "opacity-50 cursor-not-allowed",
            className,
          )}
          aria-label={label}
          disabled={disabled}
          {...props}
        >
          {icon}
        </button>

        {badge && (
          <span
            className={cn(
              "absolute -top-1 -right-1 flex items-center justify-center",
              "rounded-full text-xs font-medium text-white",
              typeof badge === "number" ? "min-w-5 h-5 px-1" : "w-2 h-2",
              badgeColor,
            )}
            aria-label={typeof badge === "number" ? `${badge} notifications` : "Has notifications"}
          >
            {typeof badge === "number" && (badge > 99 ? "99+" : badge)}
          </span>
        )}
      </div>
    );
  },
);

IconButton.displayName = "IconButton";
